import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventAPI } from '../lib/eventApi';
import PlayerJoinPanel from '../components/events/PlayerJoinPanel';
import AllCourtsView from '../components/events/AllCourtsView';
import PlayerWeekHistoryList from '../components/events/PlayerWeekHistoryList';
import StandingsPanel from '../components/events/StandingsPanel';
import EventAboutPanel from '../components/events/EventAboutPanel';
import FindSubTab from '../components/events/FindSubTab';
import EventPhotoGallery from '../components/events/EventPhotoGallery';
import { ArrowLeft, CalendarBlank, ChartBar, ClockCounterClockwise, BookOpen, UserSwitch } from '@phosphor-icons/react';

// Player-facing session detail page.
//   • If session.status === 'registration_open' or 'registration_closed' or 'draft':
//        shows the PlayerJoinPanel.
//   • If session.status === 'in_progress' or 'completed':
//        shows tabs (Tonight / Standings / History).
export default function PlayerSessionDetailPage() {
  const { sessionId } = useParams();
  const { user, managedSessions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [myReg, setMyReg] = useState(null);
  const [spots, setSpots] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await eventAPI.getSession(sessionId);
      setSession(r.data.session);
      setMyReg(r.data.my_registration);
      setSpots(r.data.spots);
      setRegistrations(r.data.registrations || []);
      setRefreshKey((k) => k + 1);
      if (r.data.session.status === 'in_progress' || r.data.session.status === 'completed') {
        try {
          const w = await eventAPI.getWeek(sessionId, r.data.session.current_week);
          setCurrentWeek(w.data);
        } catch {/* week may not exist; ignore */}
      }
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="player-session-loading">
        <div className="h-8 w-40 bg-[#f0ece3] rounded animate-pulse" />
        <div className="h-32 bg-[#f0ece3] rounded-2xl animate-pulse" />
        <div className="h-24 bg-[#f0ece3] rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="space-y-4" data-testid="player-session-error">
        <button onClick={() => navigate('/events')} className="text-sm text-[#015e3f] flex items-center gap-1">
          <ArrowLeft size={14} /> Back to events
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error || 'Session not found'}
        </div>
      </div>
    );
  }

  const isStarted = session.status === 'in_progress' || session.status === 'completed';

  const defaultTab = 'assignments';
  const activeTab = searchParams.get('view') || defaultTab;
  const setActiveTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === defaultTab) next.delete('view'); else next.set('view', id);
    setSearchParams(next, { replace: true });
  };

  const isAdminOrEM = user?.role === 'admin' || (Array.isArray(managedSessions) && managedSessions.includes(session.id));

  const tabs = [
    { id: 'assignments', label: 'Assignments', icon: CalendarBlank },
    { id: 'find-sub', label: 'Find Sub', icon: UserSwitch },
    { id: 'standings', label: 'Standings', icon: ChartBar },
    { id: 'history', label: 'History', icon: ClockCounterClockwise },
    { id: 'about', label: 'About', icon: BookOpen },
  ];

  return (
    <div className="space-y-6" data-testid="player-session-page">
      <button
        onClick={() => navigate('/events')}
        className="text-sm text-[#015e3f] flex items-center gap-1"
        data-testid="back-to-events-btn"
      >
        <ArrowLeft size={14} /> Back to events
      </button>

      <header>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight" data-testid="session-name">
          {session.name}
        </h1>
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#6a766e]">
          <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
            {session.gender === 'mens' ? "Men's Night" : 'Ladies Night'}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
            {session.num_courts} × {session.players_per_court}
          </span>
          {isStarted && (
            <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
              Week {session.current_week} of {session.weeks_planned}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-[#015e3f]/10 text-[#015e3f] font-medium" data-testid="session-status">
            {session.status.replace(/_/g, ' ')}
          </span>
        </div>
      </header>

      <EventPhotoGallery flyerUrl={session.flyer_url} eventName={session.name} kind="session" eventId={session.id} />

      {!isStarted && (
        <>
          <PlayerJoinPanel
            session={session}
            spots={spots}
            registrations={registrations}
            myRegistration={myReg}
            onJoined={load}
          />
          {/* About panel — visible even before the session starts so users
              can read the format before deciding whether to register. */}
          <EventAboutPanel session={session} />
        </>
      )}

      {isStarted && (
        <>
          <div className="flex gap-1 bg-[#f0ece3] p-1 rounded-xl overflow-x-auto" data-testid="player-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                data-testid={`player-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap ${
                  activeTab === t.id
                    ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm'
                    : 'text-[#6a766e] hover:text-[#013523]'
                }`}
              >
                <t.icon size={14} weight={activeTab === t.id ? 'fill' : 'regular'} />
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'assignments' && (
            <>
              {myReg && currentWeek?.courts?.some((c) =>
                c.slots.some((s) => s.profile_id === user.id)
              ) === false && (
                <div
                  className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800"
                  data-testid="registered-but-not-on-roster"
                >
                  You're registered, but you're not on the roster for Week {currentWeek?.week?.week_number}. Check with the admin.
                </div>
              )}
              <AllCourtsView
                week={currentWeek}
                myProfileId={user.id}
                onFindSubClick={() => setActiveTab('find-sub')}
              />
            </>
          )}
          {activeTab === 'find-sub' && (
            <FindSubTab
              sessionId={session.id}
              currentWeek={session.current_week}
              isAdminOrEM={isAdminOrEM}
              myRegistrationRole={myReg?.role || null}
            />
          )}
          {activeTab === 'standings' && (
            <StandingsPanel sessionId={session.id} refreshKey={refreshKey} highlightProfileId={user.id} />
          )}
          {activeTab === 'history' && (
            <PlayerWeekHistoryList
              sessionId={session.id}
              currentWeek={session.current_week}
              myProfileId={user.id}
              everyoneView
            />
          )}
          {activeTab === 'about' && (
            <EventAboutPanel session={session} />
          )}
        </>
      )}
    </div>
  );
}
